// SEQ-180 typed client for the `pnl_analytics_query` RPC (plan section 6.5).
// Types only, plus one thin call wrapper and a runtime decoder.
//
// THE X.1 RULE THIS FOLLOWS: supabase-js resolves `{ data, error }` on a
// non-2xx response; it does not reject the promise, so `.catch()` never runs.
// `callPnlAnalytics` checks `error` explicitly and throws, exactly like every
// other Supabase call in this codebase (e.g. src/hooks/usePnlMonthlyData.ts,
// which throws on `reportsError`/`pnlError` rather than chaining `.catch()`).
//
// ROUND 2 (code review): the meta field names below are camelCase
// (unmappedCategories, unmappedLocations, payrollAsAt, generatedAt), per the
// team lead's brief. At the time this was written,
// supabase/migrations/20260908000500_pnl_analytics_query.sql still returned
// snake_case meta keys (unmapped_categories, unmapped_locations,
// payroll_as_at, generated_at; see that file's `RETURN jsonb_build_object`
// near the end of the function body), so the database agent's round 2 rename
// had not landed in the worktree yet. Built to the camelCase names as
// instructed. `decodePnlAnalyticsResponse` below will throw a descriptive
// error naming the exact missing/mistyped field the moment a real RPC
// response is decoded against a mismatched migration, rather than silently
// producing `undefined`s, so this drift cannot pass unnoticed.
//
// `pnl_analytics_query` does not exist in generated Supabase types (owned by
// the database agent on this ticket), so `rpc()`'s first argument needs one
// narrow, local escape (below) rather than a blanket `as any` on the whole
// client. The RESPONSE is never trusted to a type assertion: it is validated
// field by field by `decodePnlAnalyticsResponse`, so a drifted RPC shape
// throws immediately instead of silently mistyping every caller.
//
// ROUND 3 (code review, parity harness divergence 4): `PnlGroupBy` below used
// to be imported, unchanged, from pnlAnalytics.ts, whose type was
// `project | month | project_month | company`, while the RPC's CHECK then
// accepted neither `project_month` nor `company`, so both type-checked here
// and raised SQLSTATE 22023 at the RPC. `PnlGroupBy` is now this module's own
// type, matching the RPC's vocabulary exactly (see the type below for the
// migrations that widened it, `project_month` last, by SEQ-193).
// `pnlAnalytics.ts`'s client-side rollup vocabulary is `PnlRollupGrain`, a
// separate type on purpose: fetch from the RPC at a grain it supports, then
// optionally roll further client side with `computePnlBuckets`.
//
// SEQ-180 PHASE 2 (plan D17, D22, section 6.3): the RPC's vocabulary gains
// `company` (bucket key is the organisation id alone, label "All projects"),
// `project_manager` and `site_manager` (two new free-text project columns,
// null grouped under "(no project manager)" / "(no site manager)"), so
// `company` is now a real RPC grain and no longer the round 3 example of a
// stale value; `project_month` was client-side only until SEQ-193, which made it a real RPC grain (a row carries both projectId and periodMonth). Every row also
// carries a `metricsAsAt` object naming, per `latest`-aggregated metric,
// the month its snapshot value is as at (`{}` when there is none); the
// decoder validates it field by field like everything else.

import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * The RPC's own `groupBy` vocabulary, exactly matching the CHECK in
 * `pnl_analytics_query` (supabase/migrations/20260910000300_pnl_analytics_query_v3.sql,
 * which widened the 20260908000500 list by `company`, `project_manager` and
 * `site_manager`, and 20261006000100_seq193_pnl_project_month_grain.sql, which
 * added `project_month`). Distinct from `PnlRollupGrain` in pnlAnalytics.ts on
 * purpose (Round 3, see file header): the two must never be interchanged.
 */
export type PnlGroupBy =
  | "project"
  | "month"
  | "quarter"
  | "calendar_year"
  | "financial_year"
  | "builder"
  | "status"
  | "company"
  | "project_manager"
  | "site_manager"
  // SEQ-193: one row per (project, month); needs both dates, at most 24 months.
  | "project_month";

/** The complete, exact set of values the RPC accepts for `groupBy`. */
export const PNL_GROUP_BY_VALUES: readonly PnlGroupBy[] = [
  "project",
  "month",
  "quarter",
  "calendar_year",
  "financial_year",
  "builder",
  "status",
  "company",
  "project_manager",
  "site_manager",
  "project_month",
];

/** Runtime guard: `filters.groupBy` can come from a saved widget or external JSON, so a TypeScript type alone does not stop a stale `'project_month'` value reaching the RPC. */
export function isPnlGroupBy(value: string): value is PnlGroupBy {
  return (PNL_GROUP_BY_VALUES as readonly string[]).includes(value);
}

/** The RPC's request shape, plan section 6.5. */
export interface PnlAnalyticsFilters {
  /** ISO 'YYYY-MM-DD', or null for no lower bound (project lifetime). */
  from: string | null;
  /** ISO 'YYYY-MM-DD', or null for no upper bound. */
  to: string | null;
  projectIds: string[];
  statuses: string[];
  groupBy: PnlGroupBy;
  metrics: string[];
  schemaVersion: 1;
}

/**
 * One row of the RPC's response: one bucket at the requested grain. Field
 * names match the SQL's jsonb_build_object exactly (camelCase).
 *
 * Round 5 (security re-audit): `organizationId` is new. Buckets are now
 * scoped per organisation for multi-org callers, so every row carries the
 * uuid of the organisation it belongs to. Required, not optional: a row with
 * no organisation is not a value this client should ever silently accept.
 * The client-side rollup (`computePnlBuckets` in pnlAnalytics.ts) does not
 * need it: production is single-org today, and that function only ever
 * groups by project/month/company, never by organisation, so this field is
 * carried on `PnlAnalyticsRow` for callers to read directly, not threaded
 * into `PnlAnalyticsInputRow`.
 */
export interface PnlAnalyticsRow {
  groupKey: string;
  groupLabel: string;
  projectId: string | null;
  organizationId: string;
  periodMonth: string | null;
  metrics: Record<string, number | null>;
  /**
   * SEQ-180 phase 2 (plan section 6.3). For each `latest`-aggregated metric
   * that has a value in this bucket, the ISO 'YYYY-MM-DD' month the value is
   * a snapshot of, keyed by metric key. Always present on every row of the
   * v3 RPC (20260910000300 emits `{}` when the row has no such metric), so
   * the decoder treats it as required: a row without it is a drifted RPC,
   * not an older shape to tolerate. A widget header renders it as
   * "as at Aug 2026", because a snapshot with an invisible date is its own
   * trap.
   */
  metricsAsAt: Record<string, string>;
}

/** Auditability block (AC38): what stayed unmapped or stale behind this response. */
export interface PnlAnalyticsMeta {
  unmappedCategories: string[];
  unmappedLocations: number;
  payrollAsAt: string | null;
  generatedAt: string;
}

export interface PnlAnalyticsResponse {
  rows: PnlAnalyticsRow[];
  meta: PnlAnalyticsMeta;
}

/** The narrow slice of SupabaseClient this module actually calls, typed against pnl_analytics_query specifically instead of `any`. */
interface PnlAnalyticsRpcClient {
  rpc(
    fn: "pnl_analytics_query",
    args: { p_filters: PnlAnalyticsFilters },
  ): PromiseLike<{ data: unknown; error: { message: string } | null }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function decodeRow(value: unknown, index: number): PnlAnalyticsRow {
  if (!isRecord(value)) {
    throw new Error(`pnl_analytics_query response: rows[${index}] must be an object`);
  }
  const { groupKey, groupLabel, projectId, organizationId, periodMonth, metrics, metricsAsAt } = value;

  if (typeof groupKey !== "string") {
    throw new Error(`pnl_analytics_query response: rows[${index}].groupKey must be a string`);
  }
  if (typeof groupLabel !== "string") {
    throw new Error(`pnl_analytics_query response: rows[${index}].groupLabel must be a string`);
  }
  if (projectId !== null && typeof projectId !== "string") {
    throw new Error(`pnl_analytics_query response: rows[${index}].projectId must be a string or null`);
  }
  if (typeof organizationId !== "string" || organizationId.length === 0) {
    throw new Error(`pnl_analytics_query response: rows[${index}].organizationId must be a non-empty string`);
  }
  if (periodMonth !== null && typeof periodMonth !== "string") {
    throw new Error(`pnl_analytics_query response: rows[${index}].periodMonth must be a string or null`);
  }
  if (!isRecord(metrics)) {
    throw new Error(`pnl_analytics_query response: rows[${index}].metrics must be an object`);
  }
  for (const [key, v] of Object.entries(metrics)) {
    if (v !== null && typeof v !== "number") {
      throw new Error(
        `pnl_analytics_query response: rows[${index}].metrics["${key}"] must be a number or null, got ${typeof v}`,
      );
    }
  }

  // Phase 2: required on every row (the v3 RPC always emits it, `{}` when
  // the row has no latest metric). Each entry maps a metric key to the ISO
  // month its snapshot value is as at. A missing or malformed value throws
  // by field name, like every other field, rather than being dropped
  // silently (which is what an undecoded field would do).
  if (!isRecord(metricsAsAt) || Array.isArray(metricsAsAt)) {
    throw new Error(`pnl_analytics_query response: rows[${index}].metricsAsAt must be an object (use {} when the row has no latest metric)`);
  }
  const asAt: Record<string, string> = {};
  for (const [key, v] of Object.entries(metricsAsAt)) {
    if (typeof v !== "string" || !/^\d{4}-(0[1-9]|1[0-2])-\d{2}$/.test(v)) {
      throw new Error(
        `pnl_analytics_query response: rows[${index}].metricsAsAt["${key}"] must be an ISO YYYY-MM-DD date string, got ${JSON.stringify(v)}`,
      );
    }
    asAt[key] = v;
  }

  return {
    groupKey,
    groupLabel,
    projectId: projectId as string | null,
    organizationId,
    periodMonth: periodMonth as string | null,
    metrics: metrics as Record<string, number | null>,
    metricsAsAt: asAt,
  };
}

/**
 * Validates an `unknown` RPC payload against `PnlAnalyticsResponse` field by
 * field, throwing a descriptive error naming the exact field on any mismatch.
 * Exported so a test can decode a literal JSON sample copied from the SQL's
 * `jsonb_build_object` shape and catch drift between the two sides.
 */
export function decodePnlAnalyticsResponse(data: unknown): PnlAnalyticsResponse {
  if (!isRecord(data)) {
    throw new Error("pnl_analytics_query response: expected an object with rows and meta");
  }
  const { rows, meta } = data;

  if (!Array.isArray(rows)) {
    throw new Error("pnl_analytics_query response: rows must be an array");
  }
  if (!isRecord(meta)) {
    throw new Error("pnl_analytics_query response: meta must be an object");
  }

  const { unmappedCategories, unmappedLocations, payrollAsAt, generatedAt } = meta;

  if (!Array.isArray(unmappedCategories) || unmappedCategories.some((c) => typeof c !== "string")) {
    throw new Error("pnl_analytics_query response: meta.unmappedCategories must be a string[]");
  }
  if (typeof unmappedLocations !== "number") {
    throw new Error("pnl_analytics_query response: meta.unmappedLocations must be a number");
  }
  if (payrollAsAt !== null && typeof payrollAsAt !== "string") {
    throw new Error("pnl_analytics_query response: meta.payrollAsAt must be a string or null");
  }
  if (typeof generatedAt !== "string") {
    throw new Error("pnl_analytics_query response: meta.generatedAt must be a string");
  }

  return {
    rows: rows.map(decodeRow),
    meta: {
      unmappedCategories: unmappedCategories as string[],
      unmappedLocations,
      payrollAsAt: payrollAsAt as string | null,
      generatedAt,
    },
  };
}

/**
 * Calls `pnl_analytics_query`, throwing on any error rather than returning
 * one, so callers can rely on a resolved promise meaning success (the X.1
 * rule above). Authorisation (can_view_financials, org and project scoping)
 * happens inside the function, per plan section 6.5; a caller lacking access
 * sees the thrown Postgres error, not a partial or empty result. The response
 * is decoded (not cast) so a drifted RPC shape is caught here, not by a
 * downstream chart silently rendering `undefined`. `filters.groupBy` is
 * checked against the RPC's exact vocabulary before the network call, so a
 * stale or mistyped value throws a clear, immediate, client-side error
 * instead of a bare SQLSTATE 22023 from Postgres.
 */
export async function callPnlAnalytics(
  supabase: SupabaseClient,
  filters: PnlAnalyticsFilters,
): Promise<PnlAnalyticsResponse> {
  if (!isPnlGroupBy(filters.groupBy)) {
    throw new Error(
      `callPnlAnalytics: groupBy "${filters.groupBy}" is not one of the RPC's supported values ` +
        `(${PNL_GROUP_BY_VALUES.join(", ")}). To get a grain the RPC has no concept of, fetch at one it ` +
        `supports and roll it up client side with computePnlBuckets's PnlRollupGrain.`,
    );
  }

  const rpcClient = supabase as unknown as PnlAnalyticsRpcClient;
  const { data, error } = await rpcClient.rpc("pnl_analytics_query", { p_filters: filters });

  if (error) {
    throw new Error(`pnl_analytics_query failed: ${error.message}`);
  }

  return decodePnlAnalyticsResponse(data);
}
